import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { forgotPassword, loginUser, registerUser } from "../services/api";
import "../styles/Login.css";

const Login = ({ setAuth }) => {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [mobilePhone, setMobilePhone] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isRegister, setIsRegister] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);

  const resetMessages = () => {
    setError("");
    setSuccess("");
  };

  const goToLogin = () => {
    setIsRegister(false);
    setIsForgotPassword(false);
    resetMessages();
  };

  const goToRegister = () => {
    setIsRegister(true);
    setIsForgotPassword(false);
    resetMessages();
  };

  const goToForgotPassword = () => {
    setIsForgotPassword(true);
    setIsRegister(false);
    resetMessages();
  };

  // ✅ MANEJAR LOGIN
  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    setSuccess("");

    try {
      const result = await loginUser(username, password);

      if (result.success) {
        setAuth(true);
        navigate("/dashboard");
      } else {
        setError(result.error || "Error en login");
      }
    } catch (err) {
      console.error("Error:", err);
      setError("Error de conexión");
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ MANEJAR REGISTRO
  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    setSuccess("");
    try {
      const result = await registerUser({
        username,
        password,
        full_name: fullName,
        mobile_phone: mobilePhone,
      });
      if (result.success) {
        setSuccess(result.data?.message || "Preregistro enviado.");
        setIsRegister(false);
      } else {
        setError(result.error || "Error en preregistro");
      }
    } catch (err) {
      setError("Error de conexión");
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    setSuccess("");
    try {
      const result = await forgotPassword(username);
      if (result.success) {
        setSuccess(result.data?.message || "Correo de recuperación enviado.");
        setIsForgotPassword(false);
      } else {
        setError(result.error || "No se pudo recuperar la clave");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-wrapper">
      <div className="login-container">
        <div className="login-card">
          {/* ✅ LOGO DE LA COMPAÑÍA */}
          <div className="login-logo">
            <img
              src={`${process.env.PUBLIC_URL}/immoneda.png`}
              alt="Trade IM"
              className="company-logo"
              onError={(e) => {
                e.target.style.display = "none";
                e.target.nextElementSibling.style.display = "flex";
              }}
            />
            <div className="logo-placeholder">
              <span>🚀</span>
            </div>
          </div>

          {/* ✅ HEADER */}
          <div className="login-header">
            <p className="subtitle">
              {isRegister ? "Crear nueva cuenta" : "Bienvenido de vuelta"}
            </p>
          </div>

          {/* ✅ ERROR MESSAGE */}
          {error && (
            <div className="alert alert-error">
              <span className="alert-icon">⚠️</span>
              <span className="alert-message">{error}</span>
            </div>
          )}

          {success && (
            <div className="alert alert-success">
              <span className="alert-icon">✅</span>
              <span className="alert-message">{success}</span>
            </div>
          )}

          {/* ✅ FORMULARIO */}
          <form onSubmit={isForgotPassword ? handleForgotPassword : isRegister ? handleRegister : handleLogin}>
            {/* Usuario */}
            <div className="form-group">
              <label htmlFor="username" className="form-label">
                <span className="label-icon">👤</span>
                Correo
              </label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Ingresa tu correo"
                required
                disabled={isLoading}
                className="form-input"
              />
            </div>

            {!isForgotPassword && (
              <div className="form-group">
              <label htmlFor="password" className="form-label">
                <span className="label-icon">🔒</span>
                Contraseña
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Ingresa tu contraseña"
                required
                disabled={isLoading}
                className="form-input"
              />
              </div>
            )}

            {isRegister && (
              <>
                <div className="form-group">
                  <label className="form-label"><span className="label-icon">🪪</span>Nombre</label>
                  <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Nombre completo" disabled={isLoading} className="form-input" />
                </div>
                <div className="form-group">
                  <label className="form-label"><span className="label-icon">📱</span>Teléfono móvil</label>
                  <input type="text" value={mobilePhone} onChange={(e) => setMobilePhone(e.target.value)} placeholder="3001234567" disabled={isLoading} className="form-input" />
                </div>
                <small className="form-help">
                  Los datos de panel, símbolo, Telegram y Pionex se completan cuando tu cuenta sea activada y hagas tu primer ingreso.
                </small>
              </>
            )}

            {/* Botón Submit */}
            <button
              type="submit"
              className="login-btn"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="spinner-small"></span>
                  {isForgotPassword ? "Enviando..." : isRegister ? "Registrando..." : "Conectando..."}
                </>
              ) : (
                <>
                  {isForgotPassword ? "📨 Recuperar clave" : isRegister ? "📝 Preinscribirme" : "🔐 Iniciar sesión"}
                </>
              )}
            </button>
          </form>

          {/* ✅ FOOTER */}
          <div className="login-footer">
            <p className="footer-text">
              {isForgotPassword ? "¿Recordaste tu clave?" : isRegister ? "¿Ya tienes cuenta?" : "¿No tienes cuenta?"}
            </p>
            <div className="login-footer-actions">
              {isRegister ? (
                <>
                  <button
                    type="button"
                    className="toggle-btn"
                    onClick={goToLogin}
                    disabled={isLoading}
                  >
                    🔐 Iniciar sesión
                  </button>
                  <button
                    type="button"
                    className="toggle-btn secondary"
                    onClick={goToForgotPassword}
                    disabled={isLoading}
                  >
                    📨 Recordar clave
                  </button>
                </>
              ) : isForgotPassword ? (
                <>
                  <button
                    type="button"
                    className="toggle-btn"
                    onClick={goToLogin}
                    disabled={isLoading}
                  >
                    🔐 Volver al login
                  </button>
                  <button
                    type="button"
                    className="toggle-btn secondary"
                    onClick={goToRegister}
                    disabled={isLoading}
                  >
                    📝 Registrarme aquí
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="toggle-btn"
                    onClick={goToRegister}
                    disabled={isLoading}
                  >
                    📝 Registrarme aquí
                  </button>
                  <button
                    type="button"
                    className="toggle-btn secondary"
                    onClick={goToForgotPassword}
                    disabled={isLoading}
                  >
                    📨 Recordar clave
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* ✅ DECORACIÓN */}
        <div className="login-decoration">
          <div className="decoration-circle decoration-1"></div>
          <div className="decoration-circle decoration-2"></div>
          <div className="decoration-circle decoration-3"></div>
        </div>
      </div>
    </div>
  );
};

export default Login;
